import { ColorSchemePreferenceEnum } from '../../enums/ColorSchemePreferenceEnum';

export interface OfficeHostPreferences {
  locale?: string;
  colorSchemePreference?: ColorSchemePreferenceEnum;
  highContrastEnabled?: boolean;
  reduceMotionEnabled?: boolean;
}

/** Host-backed File menu actions from OfficeGrant → File Manager. */
export type OfficeHostFileAction =
  'share' | 'move' | 'rename' | 'delete' | 'details' | 'download' | 'downloadEncrypted';

export const OfficePostMessageId = {
  AppPreferences: 'App_Preferences',
  AppPreferencesRequest: 'App_PreferencesRequest',
  AppRequestFileAction: 'App_RequestFileAction',
  /** Office → FM: open Account hub at /account/profile. */
  AppOpenAccount: 'App_OpenAccount',
  HostPostmessageReady: 'Host_PostmessageReady',
  HostPreferences: 'Host_Preferences',
  HostFileMeta: 'Host_FileMeta',
} as const;

export type OfficeHostFileMetaValues = {
  fileId: number;
  fileName?: string;
};

export type OfficeRequestFileActionValues = {
  action: OfficeHostFileAction;
  fileId: number;
};

export type OfficePostMessageEnvelope = {
  MessageId: string;
  SendTime: number;
  Values?: Record<string, unknown>;
};

export function parseOfficePostMessageData(data: unknown): OfficePostMessageEnvelope | null {
  if (!data || typeof data !== 'object' || !('MessageId' in data)) {
    return null;
  }
  return data as OfficePostMessageEnvelope;
}

export function readFilemanagerHostPreferences(state: {
  locale: string | null;
  colorSchemePreference: ColorSchemePreferenceEnum;
  highContrastEnabled: boolean;
  reduceMotionEnabled: boolean;
}): OfficeHostPreferences {
  return {
    locale: state.locale ?? 'it',
    colorSchemePreference: state.colorSchemePreference,
    highContrastEnabled: state.highContrastEnabled,
    reduceMotionEnabled: state.reduceMotionEnabled,
  };
}

export function applyFilemanagerHostPreferences(
  preferences: OfficeHostPreferences,
  actions: {
    setLocale: (locale: string) => void;
    setColorSchemePreference: (preference: ColorSchemePreferenceEnum) => void;
    setHighContrastEnabled: (enabled: boolean) => void;
    setReduceMotionEnabled: (enabled: boolean) => void;
  },
): void {
  if (typeof preferences.locale === 'string') {
    actions.setLocale(preferences.locale);
  }
  if (
    preferences.colorSchemePreference &&
    Object.values(ColorSchemePreferenceEnum).includes(preferences.colorSchemePreference)
  ) {
    actions.setColorSchemePreference(preferences.colorSchemePreference);
  }
  if (typeof preferences.highContrastEnabled === 'boolean') {
    actions.setHighContrastEnabled(preferences.highContrastEnabled);
  }
  if (typeof preferences.reduceMotionEnabled === 'boolean') {
    actions.setReduceMotionEnabled(preferences.reduceMotionEnabled);
  }
}

export function extractHostPreferencesFromMessage(
  data: OfficePostMessageEnvelope | null,
): OfficeHostPreferences | null {
  if (!data?.Values || typeof data.Values !== 'object') {
    return null;
  }
  const values = data.Values;
  const raw = values.Preferences ?? values;
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  return raw as OfficeHostPreferences;
}

export function sendHostPreferencesToEditor(
  target: MessageEventSource | null | undefined,
  targetOrigin: string,
  preferences: OfficeHostPreferences,
): void {
  if (!target || typeof (target as Window).postMessage !== 'function') {
    return;
  }
  (target as Window).postMessage(
    {
      MessageId: OfficePostMessageId.HostPreferences,
      SendTime: Date.now(),
      Values: { Preferences: preferences },
    },
    targetOrigin,
  );
}

export function announceHostPostmessageReady(
  target: MessageEventSource | null | undefined,
  targetOrigin: string,
  preferences: OfficeHostPreferences,
): void {
  if (!target || typeof (target as Window).postMessage !== 'function') {
    return;
  }
  (target as Window).postMessage(
    {
      MessageId: OfficePostMessageId.HostPostmessageReady,
      SendTime: Date.now(),
      Values: { Preferences: preferences },
    },
    targetOrigin,
  );
}

/** After a successful host rename, tell Office to refresh ribbon title / meta. */
export function sendHostFileMetaToEditor(
  target: MessageEventSource | null | undefined,
  targetOrigin: string,
  values: OfficeHostFileMetaValues,
): void {
  if (!target || typeof (target as Window).postMessage !== 'function') {
    return;
  }
  (target as Window).postMessage(
    {
      MessageId: OfficePostMessageId.HostFileMeta,
      SendTime: Date.now(),
      Values: values,
    },
    targetOrigin,
  );
}

const HOST_FILE_ACTIONS: ReadonlySet<string> = new Set([
  'share',
  'move',
  'rename',
  'delete',
  'details',
  'download',
  'downloadEncrypted',
]);

export function parseRequestFileActionValues(
  values: Record<string, unknown> | undefined,
): OfficeRequestFileActionValues | null {
  if (!values) return null;
  const { action, fileId } = values;
  if (typeof action !== 'string' || !HOST_FILE_ACTIONS.has(action)) return null;
  if (typeof fileId !== 'number' || !Number.isFinite(fileId)) return null;
  return { action: action as OfficeHostFileAction, fileId };
}
